import { Button } from "antd";
import { motion, type Variants } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";
import { Link } from "react-router";
import dashboardHero from "../../../assets/images/home/dashboard-hero.jpg";
import { CONTACT_PATH } from "../../../constants/company.constants";
import { CheckCircle } from "../../../icons/CheckCircle";

const easeOutExpo = [0.16, 1, 0.3, 1] as const;

const containerVariants: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1 } },
};

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: easeOutExpo },
  },
};

const HIGHLIGHTS = [
  // Free trial paused for now — restore when trials are back.
  // "7-day free trial",
  "Monthly or yearly billing",
];

const Hero = () => {
  return (
    <section className="relative overflow-hidden bg-white">
      {/* Background: faint grid + brand glow */}
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_right,rgba(15,23,42,0.05)_1px,transparent_1px),linear-gradient(to_bottom,rgba(15,23,42,0.05)_1px,transparent_1px)] bg-[size:56px_56px] [mask-image:radial-gradient(ellipse_70%_60%_at_50%_0%,black,transparent)]" />
      <div className="pointer-events-none absolute left-1/2 top-[-180px] h-[520px] w-[900px] max-w-full -translate-x-1/2 rounded-full bg-theme-primary/20 blur-[120px]" />

      <div className="relative mx-auto max-w-7xl px-4 pb-8 pt-16 sm:px-6 sm:pt-24 lg:px-8">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="mx-auto max-w-3xl text-center"
        >
          <motion.span
            variants={itemVariants}
            className="inline-flex items-center gap-2 rounded-full border border-theme-primary/20 bg-white/80 px-4 py-1.5 text-sm font-medium text-gray-700 backdrop-blur"
          >
            <Sparkles className="h-4 w-4 text-theme-primary" />
            AI-powered HR & payroll platform
          </motion.span>

          <motion.h1
            variants={itemVariants}
            className="mt-6 text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl lg:text-6xl lg:leading-[1.1]"
          >
            Transform your HR into{" "}
            <span className="bg-gradient-to-r from-theme-primary to-[#1B6F86] bg-clip-text text-transparent">
              Human Capital Management
            </span>
          </motion.h1>

          <motion.p
            variants={itemVariants}
            className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-gray-500"
          >
            Attendance, leave, payroll, recruitment and performance, unified in
            one platform, with real-time insights behind every decision.
          </motion.p>

          <motion.div
            variants={itemVariants}
            className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row"
          >
            <Link to={CONTACT_PATH}>
              <Button
                size="large"
                type="primary"
                className="btn-primary h-12 px-7 font-medium"
              >
                Request a Demo
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
            <Button
              size="large"
              onClick={() =>
                document
                  .getElementById("pricing")
                  ?.scrollIntoView({ behavior: "smooth" })
              }
              className="h-12 px-7 font-medium !border-gray-300 !text-gray-900 hover:!border-theme-primary hover:!text-theme-primary"
            >
              View Pricing
            </Button>
          </motion.div>

          <motion.ul
            variants={itemVariants}
            className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-gray-500"
          >
            {HIGHLIGHTS.map((item) => (
              <li key={item} className="flex items-center gap-2">
                <CheckCircle className="text-theme-primary" />
                {item}
              </li>
            ))}
          </motion.ul>
        </motion.div>

        {/* Product screenshot — full Owner Control view in a glass frame */}
        <motion.div
          initial={{ opacity: 0, y: 48 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: easeOutExpo, delay: 0.35 }}
          className="relative mx-auto mt-16 max-w-6xl sm:mt-20"
        >
          {/* Brand glow behind the frame */}
          <div className="pointer-events-none absolute -inset-x-10 top-10 bottom-10 rounded-[48px] bg-gradient-to-b from-theme-primary/30 via-theme-primary/10 to-transparent blur-3xl" />

          <div className="relative rounded-[20px] bg-white/50 p-2 shadow-[0_40px_120px_-30px_rgba(15,23,42,0.45)] ring-1 ring-gray-900/10 backdrop-blur sm:p-2.5">
            <img
              src={dashboardHero}
              alt="HRX dashboard showing owner control insights"
              className="w-full rounded-xl ring-1 ring-white/10"
            />
            {/* Top highlight line for a glassy edge */}
            <div className="pointer-events-none absolute inset-x-12 top-0 h-px bg-gradient-to-r from-transparent via-white to-transparent" />
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
